import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { ArticleSchema, BreadcrumbSchema, FAQSchema } from '@/components/SEOSchema';
import { pillarArticles } from '@/mocks/pillarArticles';
import { SITE_URL } from '@/lib/seo';

export default function PillarArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();

  const article = pillarArticles.find((a) => a.slug === slug);

  // 打开文章时跳转至顶部
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const homePath = i18n.language === 'pt' ? '/' : `/${i18n.language}/`;

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="w-full px-4 md:px-6 lg:px-10 pt-32 pb-20">
          <div className="max-w-2xl mx-auto text-center">
            <h1 className="text-2xl md:text-3xl font-semibold mb-4">Artigo não encontrado</h1>
            <p className="text-foreground-500 mb-6">O artigo que você procura não existe ou foi movido.</p>
            <Link to={homePath} className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white font-medium px-5 py-2.5 rounded-lg transition-colors">
              <i className="ri-home-line" /> {t('nav.home')}
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const currentUrl = `${SITE_URL}/guides/${article.slug}/`;

  return (
    <div className="min-h-screen bg-background-50">
      <ArticleSchema
        title={article.title}
        description={article.description}
        url={currentUrl}
        lang={i18n.language}
        datePublished={article.updated}
        dateModified={article.updated}
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: `${SITE_URL}${homePath}` },
          { name: 'Guias', url: `${SITE_URL}${homePath}` },
          { name: article.title, url: currentUrl },
        ]}
      />
      <FAQSchema faqs={article.faqs} />

      <Navbar />

      <div className="w-full px-4 md:px-6 lg:px-10 pt-24 pb-16">
        <div className="max-w-4xl mx-auto">
          {/* Breadcrumb */}
          <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-400 mb-6">
            <Link to={homePath} className="hover:text-primary-600 transition-colors">{t('nav.home')}</Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-foreground-600">{t('nav.guides')}</span>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-foreground-500">{article.title}</span>
          </nav>

          {/* Header */}
          <header className="mb-10">
            <span className="text-xs font-semibold uppercase tracking-wider text-primary-600 mb-3 block">Guia Independente · 2026</span>
            <h1 className="text-2xl md:text-4xl font-semibold mb-4 leading-tight">{article.title}</h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-400">
              <span className="flex items-center gap-1"><i className="ri-user-line" /> {t('article.author')}</span>
              <span className="flex items-center gap-1"><i className="ri-calendar-line" /> {t('article.updated')}: 27/08/2026</span>
            </div>
            <p className="mt-4 text-sm text-foreground-400 italic border-l-2 border-primary-300 pl-4">
              {t('article.disclaimer')}
            </p>
          </header>

          {/* Hero image */}
          <div className="mb-10 rounded-2xl overflow-hidden">
            <div className="w-full aspect-[16/9]">
              <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
            </div>
          </div>

          {/* Content */}
          <div className="space-y-10">
            {article.sections.map((section) => (
              <section key={section.heading}>
                <h2 className="text-xl md:text-2xl font-semibold mb-4">{section.heading}</h2>
                {section.paragraphs.map((p, i) => (
                  <p key={i} className="text-foreground-600 leading-relaxed mb-4">{p}</p>
                ))}
              </section>
            ))}

            {/* FAQ */}
            <section>
              <h2 className="text-xl md:text-2xl font-semibold mb-5">Perguntas Frequentes</h2>
              <div className="space-y-4">
                {article.faqs.map((f) => (
                  <div key={f.q} className="bg-white border border-background-200/60 rounded-xl p-5">
                    <h3 className="font-semibold mb-2">{f.q}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{f.a}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>

          {/* Internal links + CTA */}
          <div className="bg-primary-50/50 border border-primary-200/50 rounded-xl p-6 mt-10">
            <h3 className="font-semibold text-lg mb-2">Continue explorando</h3>
            <p className="text-foreground-600 text-sm mb-4">
              Veja todos os recursos, categorias e guias do CSSBuy Spreadsheet em nossa página principal.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to={homePath} className="inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-white font-medium px-5 py-2.5 rounded-lg transition-colors text-sm">
                <i className="ri-home-line" /> {t('article.backHome')}
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 bg-white hover:bg-background-50 text-foreground-700 font-medium px-5 py-2.5 rounded-lg border border-background-200 transition-colors text-sm"
              >
                {t('nav.mainSite')} <i className="ri-external-link-line" />
              </a>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}